<template>
  <v-container fluid class="content-container">
    <v-row class="mb-2" align="center">
      <v-col cols="12" md="6">
        <h2>Parrainage — Récompenses</h2>
      </v-col>
      <v-col cols="12" md="6" class="text-md-right">
        <v-chip class="mr-2" color="orange" text-color="white">
          À payer : {{ formatAmount(dueTotal) }} HTG
        </v-chip>
        <v-btn icon @click="load"><v-icon>mdi-refresh</v-icon></v-btn>
      </v-col>
    </v-row>

    <v-tabs v-model="tab" class="mb-3">
      <v-tab>À payer ({{ dueRewards.length }})</v-tab>
      <v-tab>Payées</v-tab>
    </v-tabs>

    <!-- Filtres (onglet Payées uniquement) -->
    <v-card v-if="tab === 1" class="filter-card mb-4">
      <v-card-text class="filter-content">
        <v-row align="center">
          <v-col cols="12" sm="6" md="3">
            <v-menu
              v-model="fromMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="paidFilters.from"
                  label="Date début"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="paidFilters.from"
                no-title
                :max="paidFilters.to"
                @input="fromMenu = false; loadPaid()"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-menu
              v-model="toMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="paidFilters.to"
                  label="Date fin"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="paidFilters.to"
                no-title
                :min="paidFilters.from"
                @input="toMenu = false; loadPaid()"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="paidFilters.search"
              prepend-inner-icon="mdi-magnify"
              placeholder="Rechercher un parrain, un business…"
              outlined
              dense
              hide-details
              clearable
              class="table-search"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3" class="text-md-right">
            <v-chip
              small
              outlined
              color="primary"
              class="filter-chip mr-2"
              @click="resetPaidDateRange"
            >
              <v-icon left x-small>mdi-calendar-refresh</v-icon>
              30 derniers jours
            </v-chip>
            <v-chip class="results-chip" small>
              {{ paidRewards.length }} récompense(s)
            </v-chip>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-data-table
      :headers="headers"
      :items="tab === 0 ? dueRewards : paidRewards"
      :loading="loading"
      :items-per-page="25"
      loading-text="Chargement..."
      no-data-text="Aucune récompense"
      class="modern-table elevation-1"
    >
      <template v-slot:[`item.referrer`]="{ item }">
        <div v-if="item.referrer">
          <strong>{{ item.referrer.first_name }} {{ item.referrer.last_name }}</strong>
          <div class="grey--text">{{ item.referrer.phone }}</div>
        </div>
        <span v-else>—</span>
      </template>

      <template v-slot:[`item.payout`]="{ item }">
        <div v-if="item.referrer">
          <v-chip x-small :color="payoutColor(item.referrer.payout_method)" text-color="white">
            {{ item.referrer.payout_method }}
          </v-chip>
          <div class="grey--text caption">{{ payoutSummary(item.referrer) }}</div>
        </div>
        <span v-else>—</span>
      </template>

      <template v-slot:[`item.business`]="{ item }">
        {{ item.business ? item.business.name : '—' }}
      </template>

      <template v-slot:[`item.amount`]="{ item }">
        {{ formatAmount(item.amount) }} {{ item.currency }}
      </template>

      <template v-slot:[`item.created_at`]="{ item }">
        {{ formatDate(item.created_at) }}
      </template>

      <template v-slot:[`item.paid_at`]="{ item }">
        <span v-if="item.paid_at">{{ formatDate(item.paid_at) }}</span>
        <span v-else>—</span>
        <div v-if="item.payment_reference" class="grey--text caption">
          Réf : {{ item.payment_reference }}
        </div>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-btn
          v-if="item.status === 'DUE'"
          small color="success" dark
          @click="openPayDialog(item)"
        >
          <v-icon left small>mdi-cash-check</v-icon> Marquer payée
        </v-btn>
      </template>
    </v-data-table>

    <v-dialog v-model="payDialog" max-width="480">
      <v-card v-if="selected">
        <v-card-title>Marquer la récompense payée</v-card-title>
        <v-card-text>
          <p>
            <strong>{{ selected.referrer ? `${selected.referrer.first_name} ${selected.referrer.last_name}` : '—' }}</strong>
            — {{ formatAmount(selected.amount) }} {{ selected.currency }}<br>
            {{ selected.referrer ? `${selected.referrer.payout_method} : ${payoutSummary(selected.referrer)}` : '—' }}
          </p>
          <v-form ref="payForm">
            <v-text-field
              v-model="payReference"
              label="Référence du paiement *"
              :rules="[v => !!v || 'La référence est requise']"
              outlined dense
            />
            <v-textarea v-model="payNote" label="Note (optionnel)" outlined dense rows="2" />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="payDialog = false">Annuler</v-btn>
          <v-btn color="success" :loading="paying" @click="confirmPay">Confirmer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="4000">
      {{ snackbarText }}
    </v-snackbar>
  </v-container>
</template>

<script>
import moment from 'moment'
import numeral from 'numeral'

export default {
  name: 'ReferralRewardsView',
  data: () => ({
    tab: 0,
    loading: false,
    dueRewards: [],
    paidRewards: [],
    paidFilters: {
      from: moment().subtract(30, 'days').format('YYYY-MM-DD'),
      to: moment().format('YYYY-MM-DD'),
      search: '',
    },
    fromMenu: false,
    toMenu: false,
    searchTimeout: null,
    payDialog: false,
    selected: null,
    payReference: '',
    payNote: '',
    paying: false,
    snackbar: false,
    snackbarText: '',
    snackbarColor: 'success',
    headers: [
      { text: 'Parrain', value: 'referrer', sortable: false },
      { text: 'Paiement (payout)', value: 'payout', sortable: false },
      { text: 'Business filleul', value: 'business', sortable: false },
      { text: 'Montant', value: 'amount' },
      { text: 'Créée le', value: 'created_at' },
      { text: 'Payée le', value: 'paid_at' },
      { text: 'Actions', value: 'actions', sortable: false },
    ],
  }),
  computed: {
    dueTotal() {
      return this.dueRewards.reduce((sum, r) => sum + Number(r.amount), 0)
    },
  },
  watch: {
    'paidFilters.search'() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => this.loadPaid(), 300)
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      try {
        await Promise.all([this.loadDue(), this.loadPaid()])
      } finally {
        this.loading = false
      }
    },
    async loadDue() {
      try {
        const res = await this.$axios.get('/referral-rewards', { params: { status: 'DUE' } })
        this.dueRewards = res.data.data
      } catch (e) {
        this.notify('Erreur lors du chargement des récompenses à payer', 'error')
      }
    },
    resetPaidDateRange() {
      this.paidFilters.from = moment().subtract(30, 'days').format('YYYY-MM-DD')
      this.paidFilters.to = moment().format('YYYY-MM-DD')
      this.loadPaid()
    },
    async loadPaid() {
      try {
        const res = await this.$axios.get('/referral-rewards', {
          params: {
            status: 'PAID',
            from: this.paidFilters.from || undefined,
            to: this.paidFilters.to || undefined,
            search: this.paidFilters.search || undefined,
          },
        })
        this.paidRewards = res.data.data
      } catch (e) {
        this.notify('Erreur lors du chargement des récompenses payées', 'error')
      }
    },
    openPayDialog(reward) {
      this.selected = reward
      this.payReference = ''
      this.payNote = ''
      this.payDialog = true
      this.$nextTick(() => { this.$refs.payForm && this.$refs.payForm.resetValidation() })
    },
    async confirmPay() {
      if (!this.$refs.payForm.validate()) return
      this.paying = true
      try {
        await this.$axios.post(`/referral-rewards/${this.selected.id}/pay`, {
          payment_reference: this.payReference,
          note: this.payNote || undefined,
        })
        this.payDialog = false
        this.notify('Récompense marquée payée', 'success')
        await this.load()
      } catch (e) {
        this.notify('Erreur : ' + (e.response?.data?.message || 'échec du paiement'), 'error')
      } finally {
        this.paying = false
      }
    },
    notify(text, color) {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    },
    payoutSummary(referrer) {
      const d = referrer.payout_details || {}
      if (referrer.payout_method === 'BANK') {
        const account = `${d.account_number || ''}${d.currency ? ' ' + d.currency : ''}`
        return `${d.bank_name || ''} ${d.account_type || ''} ${account} (${d.account_name || ''})`
      }
      return `${d.wallet_phone || ''} (${d.account_name || ''})`
    },
    payoutColor(method) {
      return { MONCASH: 'red', NATCASH: 'blue', BANK: 'teal' }[method] || 'grey'
    },
    formatAmount(v) {
      return numeral(v).format('0,0')
    },
    formatDate(v) {
      return moment(v).format('DD/MM/YYYY HH:mm')
    },
  },
}
</script>

<style lang="scss" scoped>
.content-container {
  padding: 0 24px 24px;
}

.filter-card {
  border-radius: 12px !important;
}

.filter-content {
  padding: 16px 20px !important;
}

.table-search {
  ::v-deep .v-input__slot {
    min-height: 40px !important;
    border-radius: 8px !important;
  }
}

.filter-chip {
  cursor: pointer;
  font-weight: 500 !important;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }
}

.results-chip {
  font-weight: 500;
}

.modern-table {
  ::v-deep th {
    font-weight: 600 !important;
    font-size: 12px !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
  }

  ::v-deep td {
    padding: 16px !important;
  }

  ::v-deep tbody tr:hover {
    background: rgba(0, 63, 116, 0.04);
  }
}

.theme--dark .modern-table ::v-deep tbody tr:hover {
  background: rgba(255, 255, 255, 0.04);
}
</style>
